/** @format */

import { isPlatformBrowser } from '@angular/common';
import { Component, EventEmitter, Inject, Input, Output, PLATFORM_ID } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import {
	CertonaRecommendations,
	CertonaScheme,
	CertonaService
} from '@product-recommendations/data-access';
import {
	AddOnCard,
	AvailabilityObject,
	AvailableCombinations,
	Chain,
	NestProduct,
	NestProductSKU,
	ProductsData,
	ProductSku
} from '@shared/data-access/interfaces';
import { AddOnRelevantCertonaInfo, CertonaParsedForAddOns } from './certona-add-ons-info.interface';
import { ProductService } from '@shared/data-access/services';
import { AnalyticsService } from '@shared/utils/services';

@Component({
    selector: 'shared-components-base-add-on-product-parser',
    template: ` <p>Base Add-On Product Parser works!</p> `,
    standalone: false
})
export class BaseAddOnProductParserComponent {
	// the certona scheme we look for to populate add ons
	@Input()
	public providedScheme: string;

	@Input()
	public showHeader = true;

	@Output()
	public schemeLoaded: EventEmitter<any> = new EventEmitter<any>();

	// default title if certona does not provide one
	public certonaTitle: string;

	public product: any;

	// number of cards to show
	public numberOfCardsToShow = 4;

	// get overwritten to 4 on mobile
	public numColorsToShow = 13;

	// data that is used to populate the ui
	public cardDataList: AddOnCard[] = [];

	// card data after its been sliced down to sized
	public slicedCardDataList: AddOnCard[] = [];

	// sets to true once we trigger the analytic event so we dont double fire
	public noProductAnalyticTriggered = false;

	/**
	 * @description Determines if we need to hide product attributes based upon number of color attributes
	 */
	public hideAttributesForProduct: { [key: string]: boolean } = {};

	public recommendationsListener = new Subject<{ recommendationsFound: boolean }>();

	// was the scheme, passed in via "providedScheme", in the certona response
	private foundScheme = false;

	constructor(
		@Inject(PLATFORM_ID) protected platformId: any,
		protected chain: Chain,
		protected analyticsService: AnalyticsService,
		protected productService: ProductService,
		protected productContainerService: CertonaService
	) {}

	public initCertona(): void {
		this.productContainerService.certonaRecommendationsState.subscribe((recommendations) => {
			if (isPlatformBrowser(this.platformId) && recommendations) {
				// Parse the product recommendations
				this.parseCertonaRecommendations(
					recommendations.certonaRecommendations,
					this.numColorsToShow
				);
			}
		});
	}

	public parseCertonaRecommendations(
		response: CertonaRecommendations,
		numberOfColorsToShow: number
	): void {
		if (
			response &&
			response.resonance &&
			response.resonance.schemes &&
			this.cardDataList.length <= 0 &&
			!this.foundScheme
		) {
			this.foundScheme = false;
			// Iterate through the schemas
			for (const schema of response.resonance.schemes) {
				// Monetate does not set schemes with no items to have "display: no" like Certona did
				if (schema.items.length === 0) {
					schema.display = 'no';
				}
				// Check to see if the current schema is displayable and matches the filterBy input
				if (
					schema.display === 'yes' &&
					schema.scheme === this.providedScheme &&
					Array.isArray(schema.items)
				) {
					this.foundScheme = true;
					if (schema.explanation) {
						this.certonaTitle = schema.explanation;
					}

					this.schemeLoaded.emit(schema);

					const parsedCertonaInfo: CertonaParsedForAddOns =
						this.generateListOfECodes(schema);

					// Call Product Details API
					this.callProductDetailsForEachProduct(parsedCertonaInfo, numberOfColorsToShow);
				}
			}

			if (!this.foundScheme && !this.noProductAnalyticTriggered) {
				this.noProductAnalyticTriggered = true;
				this.analyticsService.triggerAnalyticsEvent('NoAddOnsShown', {
					reason: `No ${this.providedScheme} scheme in certona response`
				});
			}
		}
	}

	public generateListOfECodes(schema: CertonaScheme): CertonaParsedForAddOns {
		const addOnRelevantCertonaInfo = {};
		let listOfEcodes;
		// Iterate through the current schema items(we need to do this here to generate the listOfEcodes before calling getAddOnSkyByEcode)
		for (let certonaItemIndex = 0; certonaItemIndex < schema.items.length; certonaItemIndex++) {
			// generate list of useful certona values from certona response
			addOnRelevantCertonaInfo[schema.items[certonaItemIndex].ID.toLowerCase()] = {
				url: schema.items[certonaItemIndex].URL,
				index: certonaItemIndex,
				skuId: schema.items[certonaItemIndex].SKUID,
				imageUrl: schema.items[certonaItemIndex].ImageURL
			};
			// generate the list of eCodes to pass into the multiEcode endpoint
			listOfEcodes = listOfEcodes
				? `${listOfEcodes},${schema.items[certonaItemIndex].ID}`
				: schema.items[certonaItemIndex].ID;
		}
		return {
			listOfEcodes,
			addOnRelevantCertonaInfo
		};
	}

	private callProductDetailsForEachProduct(
		parsedCertonaInfo: CertonaParsedForAddOns,
		numberOfColorsToShow: number
	): void {
		const productAddOnViewModelArray: ProductsData[] = [];
		const eCodesArray = parsedCertonaInfo.listOfEcodes.split(',');

		for (const eCode of eCodesArray) {
			this.getProductDetailsForASingleProduct(eCode).subscribe((response: ProductsData) => {
				if (response) {
					productAddOnViewModelArray.push(response);
				}

				if (productAddOnViewModelArray.length === eCodesArray.length) {
					this.createCardDataList(
						parsedCertonaInfo,
						productAddOnViewModelArray,
						numberOfColorsToShow
					);
				}
			});
		}
	}

	private getProductDetailsForASingleProduct(eCode: string): Observable<ProductsData> {
		return this.productService.getProductByEcodeV4$(eCode).pipe(
			map((details) => {
				if (details) {
					return details;
				}
				return null;
			})
		);
	}

	private createCardDataList(
		parsedCertonaInfo: CertonaParsedForAddOns,
		productAddOnViewModelArray: ProductsData[],
		numberOfColorsToShow: number
	) {
		for (const eCodeKey of productAddOnViewModelArray) {
			// create card data object from certona response and product service response
			const addOnCardData = this.generateAddOnCardDataProductDetails(
				parsedCertonaInfo.addOnRelevantCertonaInfo,
				eCodeKey,
				numberOfColorsToShow
			);
			if (addOnCardData.rank >= 0) {
				this.cardDataList.push(addOnCardData);
			}
		}

		if (this.cardDataList && this.cardDataList.length > 0) {
			this.cardDataList.sort((a, b) => a.rank - b.rank);
			this.slicedCardDataList =
				this.numberOfCardsToShow > 0
					? this.cardDataList.slice(0, this.numberOfCardsToShow)
					: this.cardDataList;

			// Only child comps need this
			this.analyticsService.triggerAnalyticsEvent('AddOnsShown', {
				eCodes: `${this.slicedCardDataList.map((a) => a.id)}`
			});

			this.recommendationsListener.next({ recommendationsFound: true });
		} else {
			this.analyticsService.triggerAnalyticsEvent('NoAddOnsShown', {
				reason: `No products returned for:${parsedCertonaInfo.listOfEcodes}`
			});

			this.recommendationsListener.next({ recommendationsFound: false });
		}
	}

	public generateAddOnCardData(
		parsedCertonaResponse: AddOnRelevantCertonaInfo,
		productEcode: NestProduct,
		numberOfColorsToShow: number
	): AddOnCard<NestProductSKU> {
		// init card data object
		let addOnCardData = this.generateDefaultAddOnCardData<NestProductSKU>();

		// if a product was passed in and has at least one sku
		if (productEcode && Array.isArray(productEcode.skus) && productEcode.skus.length > 0) {
			const numberOfAttributes =
				Array.isArray(productEcode.defAttributes) && productEcode.defAttributes.length > 0
					? productEcode.defAttributes.length
					: 0;

			// set up beginning of image url to add color later if one is present
			addOnCardData.imageUrl = `https://dks.scene7.com/is/image/dkscdn/${productEcode.id}`;

			// if there are defAttributes loop through and set up availabilityMap for individual attribute
			if (numberOfAttributes > 0) {
				addOnCardData = this.parseProductDefAttributes(
					productEcode,
					addOnCardData,
					numberOfColorsToShow
				);

				// Determine if we need to hide attributes on inital component view
				this.hideAttributesForProduct[productEcode.id] = true;
			} else {
				addOnCardData.rank = 0;
				addOnCardData.skuItem = productEcode.skus[0];
				this.hideAttributesForProduct[productEcode.id] = false;
			}

			// all the price things
			if (productEcode.price) {
				addOnCardData.productPrice = {
					isSeePriceInCart: productEcode.price.minOffer < productEcode.price.minThreshold,
					isRangePrice: productEcode.price.minOffer !== productEcode.price.maxOffer,
					lowestPrice: productEcode.price.minOffer,
					highestPrice: productEcode.price.maxOffer
				};
				addOnCardData.shownPrice = Object.assign({}, addOnCardData.productPrice);
			}

			/*
				This method iterates through skus, creates a list of available combinations
				and creates a list of "otherDefAttributes" by combining attributes
				that arent color into a single attribute for the drop down
			*/
			addOnCardData = this.parseProductSkus(productEcode, addOnCardData);
			addOnCardData.imageUrl = `${addOnCardData.imageUrl}_is?$UTPMain$&op_sharpen=1&qlt=80`;
			addOnCardData.id = productEcode.id;
			addOnCardData.title = productEcode.title;

			// Get rank, click url destination and sku recommendation
			if (parsedCertonaResponse[productEcode.id.toLowerCase()]) {
				addOnCardData.rank =
					addOnCardData.rank > -1
						? addOnCardData.rank +
							parsedCertonaResponse[productEcode.id.toLowerCase()].index
						: parsedCertonaResponse[productEcode.id.toLowerCase()].index;
				addOnCardData.pdpSeoUrl = parsedCertonaResponse[productEcode.id.toLowerCase()].url;
				addOnCardData.skuItem =
					addOnCardData.skuItem ||
					this.checkCertonaSkuRecs(
						parsedCertonaResponse[productEcode.id.toLowerCase()].skuId,
						addOnCardData.availableCombinations
					);

				// Override image url if Certona is passing us one
				addOnCardData.imageUrl = parsedCertonaResponse[productEcode.id.toLowerCase()]
					.imageUrl
					? `${
							parsedCertonaResponse[productEcode.id.toLowerCase()].imageUrl
						}&op_sharpen=1&qlt=80`
					: addOnCardData.imageUrl;
			}
			// Don't show card if number of attribute is greater than three or number of attributes is greater than 2 and there a color attribute
			if (
				numberOfAttributes > 3 ||
				(numberOfAttributes > 2 && !addOnCardData.hasColorAttribute)
			) {
				addOnCardData.rank = -1;
			}
		}
		return addOnCardData;
	}

	public generateAddOnCardDataProductDetails(
		parsedCertonaResponse: AddOnRelevantCertonaInfo,
		productEcode: ProductsData,
		numberOfColorsToShow: number
	): AddOnCard {
		// init card data object
		let addOnCardData = this.generateDefaultAddOnCardData<ProductSku>();

		// if a product was passed in and has at least one sku
		if (
			productEcode &&
			Array.isArray(productEcode.skus) &&
			productEcode.skus.length > 0 &&
			// eslint-disable-next-line valid-typeof
			typeof productEcode.style !== undefined
		) {
			const numberOfAttributes =
				Array.isArray(productEcode.style.definingAttributes) &&
				productEcode.style.definingAttributes.length > 0
					? productEcode.style.definingAttributes.length
					: 0;

			// set up beginning of image url to add color later if one is present
			addOnCardData.imageUrl = `https://dks.scene7.com/is/image/dkscdn/${productEcode.style.partNumber}`;

			// if there are defAttributes loop through and set up availabilityMap for individual attribute
			if (numberOfAttributes > 0) {
				addOnCardData = this.parseProductDefAttributes(
					productEcode,
					addOnCardData,
					numberOfColorsToShow
				);

				// Determine if we need to hide attributes on inital component view
				this.hideAttributesForProduct[productEcode.style.partNumber] = true;
			} else {
				addOnCardData.rank = 0;
				addOnCardData.skuItem = productEcode.skus[0];
				this.hideAttributesForProduct[productEcode.style.partNumber] = false;
			}

			// all the price things
			if (productEcode.style.prices) {
				addOnCardData.productPrice = {
					isSeePriceInCart: false, //productEcode.style.prices.minofferprice < productEcode.style.prices.minThreshold,
					isRangePrice:
						productEcode.style.prices.minofferprice !==
						productEcode.style.prices.maxofferprice,
					lowestPrice: Number.parseFloat(productEcode.style.prices.minofferprice),
					highestPrice: Number.parseFloat(productEcode.style.prices.maxofferprice)
				};
				addOnCardData.shownPrice = Object.assign({}, addOnCardData.productPrice);
			}

			/*
				This method iterates through skus, creates a list of available combinations
				and creates a list of "otherDefAttributes" by combining attributes
				that arent color into a single attribute for the drop down
			*/
			addOnCardData = this.parseProductDetailsSkus(productEcode, addOnCardData);
			addOnCardData.imageUrl = `${addOnCardData.imageUrl}_is?$UTPMain$&op_sharpen=1&qlt=80`;
			addOnCardData.id = productEcode.style.partNumber;
			addOnCardData.title = productEcode.style.name;

			// Get rank, click url destination and sku recommendation
			if (parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()]) {
				addOnCardData.rank =
					addOnCardData.rank > -1
						? addOnCardData.rank +
							parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()].index
						: parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()].index;
				addOnCardData.pdpSeoUrl =
					parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()].url;
				addOnCardData.skuItem =
					addOnCardData.skuItem ||
					this.checkCertonaSkuRecs(
						parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()].skuId,
						addOnCardData.availableCombinations
					);

				// Override image url if Certona is passing us one
				addOnCardData.imageUrl = parsedCertonaResponse[
					productEcode.style.partNumber.toLowerCase()
				].imageUrl
					? `${
							parsedCertonaResponse[productEcode.style.partNumber.toLowerCase()]
								.imageUrl
						}&op_sharpen=1&qlt=80`
					: addOnCardData.imageUrl;
			}
			// Don't show card if number of attribute is greater than three or number of attributes is greater than 2 and there a color attribute
			if (
				numberOfAttributes > 3 ||
				(numberOfAttributes > 2 && !addOnCardData.hasColorAttribute)
			) {
				addOnCardData.rank = -1;
			}
		}
		return addOnCardData;
	}

	public parseProductDefAttributes<T extends NestProduct | ProductsData>(
		productEcode: T,
		addOnCardData: AddOnCard<T['skus'][0]>,
		numberOfColorsToShow: number
	): AddOnCard<T['skus'][0]> {
		const tempAddOnCardData = Object.assign({}, addOnCardData);
		let newColorSwatchString = '';
		let shownColorValues = '';
		let numberOfColorsNotShown = 0;

		const definingAttribute = !('style' in productEcode)
			? productEcode.defAttributes
			: productEcode.style.definingAttributes;

		for (
			let defAttrIndex = 0, defAttrlength = definingAttribute.length;
			defAttrIndex < defAttrlength;
			defAttrIndex++
		) {
			const currentDefiningAttribute = definingAttribute[defAttrIndex];
			const currentDefiningAttributeValueArray =
				'values' in currentDefiningAttribute
					? currentDefiningAttribute.values.split('|')
					: currentDefiningAttribute.value.split('|');

			// set up image url, check if has color attributes, check if it has other attributes
			if (currentDefiningAttribute.name.toLowerCase() === 'color') {
				const colorArray = currentDefiningAttributeValueArray;
				numberOfColorsNotShown =
					colorArray.length > numberOfColorsToShow
						? colorArray.length - numberOfColorsToShow
						: 0;
				let sanitizedColor = colorArray[0].replace(/\//g, '_');
				sanitizedColor = sanitizedColor.replace(/ /g, '_');
				tempAddOnCardData.imageUrl = `${tempAddOnCardData.imageUrl}_${sanitizedColor}`;
				tempAddOnCardData.hasColorAttribute = true;
			} else {
				tempAddOnCardData.hasOtherAttributes = true;
			}

			const availabilityMap = [];
			const values =
				'values' in currentDefiningAttribute
					? currentDefiningAttribute.values
					: currentDefiningAttribute.value;
			// create an avaailablityMap to know what to disable when options are selected
			for (
				let defAttrValueIndex = 0, length = values.split('|').length;
				defAttrValueIndex < length;
				++defAttrValueIndex
			) {
				if (
					currentDefiningAttribute.name.toLowerCase() !== 'color' ||
					defAttrValueIndex < numberOfColorsToShow
				) {
					if (currentDefiningAttribute.name.toLowerCase() === 'color') {
						if ('swatches' in currentDefiningAttribute) {
							newColorSwatchString =
								newColorSwatchString === ''
									? `${
											currentDefiningAttribute.swatches.split(',')[
												defAttrValueIndex
											]
										}`
									: `${newColorSwatchString},${
											currentDefiningAttribute.swatches.split(',')[
												defAttrValueIndex
											]
										}`;
						}
						shownColorValues =
							shownColorValues === ''
								? `${currentDefiningAttributeValueArray[defAttrValueIndex]}`
								: `${shownColorValues},${currentDefiningAttributeValueArray[defAttrValueIndex]}`;
					}
					availabilityMap.push({
						name: currentDefiningAttribute.name,
						value: currentDefiningAttributeValueArray[defAttrValueIndex],
						available: true,
						selected: false
					});
				}
			}

			const swatches =
				'swatches' in currentDefiningAttribute ? currentDefiningAttribute.swatches : '';

			// set up defining attribute object
			const tempDefAttribute = {
				identifier: currentDefiningAttribute.identifier,
				name: currentDefiningAttribute.name,
				value: values,
				swatches:
					currentDefiningAttribute.name.toLowerCase() === 'color'
						? newColorSwatchString
						: swatches,
				selected: false,
				availabilityMap
			};

			tempAddOnCardData.numberOfColorsNotShown = numberOfColorsNotShown;
			tempAddOnCardData.defAttributes.push(tempDefAttribute);
			tempAddOnCardData.shownColorValues = shownColorValues;

			// If there's only one color then select it
			if (availabilityMap.length === 1) {
				const singleDefAttribute = tempAddOnCardData.defAttributes[defAttrIndex];
				singleDefAttribute.availabilityMap[0].selected = true;
				singleDefAttribute.selected = true;
			}
		}
		return tempAddOnCardData;
	}

	public parseProductSkus(
		productEcode: NestProduct,
		addOnCardData: AddOnCard<NestProductSKU>
	): AddOnCard<NestProductSKU> {
		let newPrice = addOnCardData.productPrice;
		if (addOnCardData.numberOfColorsNotShown > 0 || addOnCardData.productPrice === undefined) {
			newPrice = {
				highestPrice: 0,
				lowestPrice: 0,
				isSeePriceInCart: false,
				isRangePrice: false
			};
		}

		const tempAddOnCardData = Object.assign({}, addOnCardData);

		const otherDefAttributes = [];
		const availableCombinations: AvailableCombinations[] = [];

		// if a product was passed in and has at least one sku
		if (productEcode && Array.isArray(productEcode.skus) && productEcode.skus.length > 0) {
			// for sku
			for (let i = 0, skuLength = productEcode.skus.length; i < skuLength; i++) {
				// go through skus and set up availableCombinations
				if (
					Array.isArray(productEcode.skus[i].defAttributes) &&
					productEcode.skus[i].defAttributes.length > 0
				) {
					// otherDefAttributes
					let otherDefAttributesString = '';
					let otherDefAttributesValueString = '';
					let colorString = '';
					// loop through the defining attributes to save color and otherAttribute values
					for (
						let skuDefAttIndex = 0, length = productEcode.skus[i]?.defAttributes.length;
						skuDefAttIndex < length;
						skuDefAttIndex++
					) {
						if (
							productEcode.skus[i]?.defAttributes[
								skuDefAttIndex
							].name.toLowerCase() === 'color'
						) {
							colorString = productEcode.skus[i]?.defAttributes[skuDefAttIndex].value;
						} else {
							const nameValueString = `${productEcode.skus[i]?.defAttributes[skuDefAttIndex].name}: ${productEcode.skus[i]?.defAttributes[skuDefAttIndex].value}`;
							const valueString =
								productEcode.skus[i]?.defAttributes[skuDefAttIndex].value;
							otherDefAttributesString =
								otherDefAttributesString === ''
									? nameValueString
									: `${otherDefAttributesString} | ${nameValueString}`;
							otherDefAttributesValueString =
								otherDefAttributesValueString === ''
									? valueString
									: `${otherDefAttributesValueString} | ${valueString}`;
						}
					}

					if (
						!tempAddOnCardData.hasColorAttribute ||
						tempAddOnCardData.shownColorValues.includes(colorString)
					) {
						if (
							!otherDefAttributes.find(
								(element) => element.value === otherDefAttributesString
							)
						) {
							otherDefAttributes.push({
								value: otherDefAttributesString,
								stringValue: otherDefAttributesValueString,
								selected: false,
								disabled: false
							});
						}
						newPrice.highestPrice =
							newPrice.highestPrice &&
							newPrice.highestPrice > productEcode.skus[i].price.offerPrice
								? newPrice.highestPrice
								: productEcode.skus[i].price.offerPrice;
						newPrice.lowestPrice =
							newPrice.lowestPrice > 0 &&
							newPrice.lowestPrice < productEcode.skus[i].price.offerPrice
								? newPrice.lowestPrice
								: productEcode.skus[i].price.offerPrice;
						newPrice.isSeePriceInCart =
							productEcode.skus[i].price.offerPrice <
							productEcode.skus[i].price.thresholdPrice;

						availableCombinations.push({
							sku: productEcode.skus[i],
							color: colorString,
							otherDefAttributes: otherDefAttributesString
						});
					}
				}
			}
		}
		newPrice.isRangePrice = newPrice.highestPrice !== newPrice.lowestPrice;

		tempAddOnCardData.productPrice = Object.assign({}, newPrice);
		tempAddOnCardData.shownPrice = Object.assign({}, newPrice);
		tempAddOnCardData.otherDefAttributes = otherDefAttributes;
		tempAddOnCardData.availableCombinations = availableCombinations;

		return tempAddOnCardData;
	}

	public parseProductDetailsSkus(
		productEcode: ProductsData,
		addOnCardData: AddOnCard<ProductSku>
	): AddOnCard<ProductSku> {
		let newPrice = addOnCardData.productPrice;
		if (addOnCardData.numberOfColorsNotShown > 0 || addOnCardData.productPrice === undefined) {
			newPrice = {
				highestPrice: 0,
				lowestPrice: 0,
				isSeePriceInCart: false,
				isRangePrice: false
			};
		}

		const tempAddOnCardData = Object.assign({}, addOnCardData);

		const otherDefAttributes = [];
		const availableCombinations: AvailableCombinations[] = [];

		// if a product was passed in and has at least one sku
		if (productEcode && Array.isArray(productEcode.skus) && productEcode.skus.length > 0) {
			// for sku
			for (let i = 0, skuLength = productEcode.skus.length; i < skuLength; i++) {
				// go through skus and set up availableCombinations
				if (
					Array.isArray(productEcode.skus[i].definingAttributes) &&
					productEcode.skus[i].definingAttributes.length > 0
				) {
					// otherDefAttributes
					let otherDefAttributesString = '';
					let otherDefAttributesValueString = '';
					let colorString = '';
					// loop through the defining attributes to save color and otherAttribute values
					for (
						let skuDefAttIndex = 0,
							length = productEcode.skus[i]?.definingAttributes.length;
						skuDefAttIndex < length;
						skuDefAttIndex++
					) {
						if (
							productEcode.skus[i]?.definingAttributes[
								skuDefAttIndex
							].name.toLowerCase() === 'color'
						) {
							colorString =
								productEcode.skus[i]?.definingAttributes[skuDefAttIndex].value;
						} else {
							const nameValueString = `${productEcode.skus[i]?.definingAttributes[skuDefAttIndex].name}: ${productEcode.skus[i]?.definingAttributes[skuDefAttIndex].value}`;
							const valueString =
								productEcode.skus[i]?.definingAttributes[skuDefAttIndex].value;
							otherDefAttributesString =
								otherDefAttributesString === ''
									? nameValueString
									: `${otherDefAttributesString} | ${nameValueString}`;
							otherDefAttributesValueString =
								otherDefAttributesValueString === ''
									? valueString
									: `${otherDefAttributesValueString} | ${valueString}`;
						}
					}

					if (
						!tempAddOnCardData.hasColorAttribute ||
						tempAddOnCardData.shownColorValues.includes(colorString)
					) {
						if (
							!otherDefAttributes.find(
								(element) => element.value === otherDefAttributesString
							)
						) {
							otherDefAttributes.push({
								value: otherDefAttributesString,
								stringValue: otherDefAttributesValueString,
								selected: false,
								disabled: false
							});
						}
						newPrice.highestPrice =
							newPrice.highestPrice &&
							newPrice.highestPrice > productEcode.skus[i].prices.offerPrice
								? newPrice.highestPrice
								: productEcode.skus[i].prices.offerPrice;
						newPrice.lowestPrice =
							newPrice.lowestPrice > 0 &&
							newPrice.lowestPrice < productEcode.skus[i].prices.offerPrice
								? newPrice.lowestPrice
								: productEcode.skus[i].prices.offerPrice;
						newPrice.isSeePriceInCart = false; //productEcode.skus[i].prices.offerPrice < productEcode.skus[i].price.thresholdPrice;

						availableCombinations.push({
							sku: productEcode.skus[i],
							color: colorString,
							otherDefAttributes: otherDefAttributesString
						});
					}
				}
			}
		}
		newPrice.isRangePrice = newPrice.highestPrice !== newPrice.lowestPrice;

		tempAddOnCardData.productPrice = Object.assign({}, newPrice);
		tempAddOnCardData.shownPrice = Object.assign({}, newPrice);
		tempAddOnCardData.otherDefAttributes = otherDefAttributes;
		tempAddOnCardData.availableCombinations = availableCombinations;

		return tempAddOnCardData;
	}

	public checkCertonaSkuRecs(skuId: string, availableCombinations: AvailableCombinations[]) {
		if (!skuId || !availableCombinations) {
			return undefined;
		}

		let index = 0;
		let skuItem;

		while (!skuItem && index < availableCombinations.length) {
			const combo = availableCombinations[index];
			if (
				('id' in combo.sku && skuId === combo.sku.id) ||
				('partNumber' in combo.sku && skuId === combo.sku.partNumber)
			) {
				skuItem = combo.sku;
			}
			++index;
		}

		return skuItem;
	}

	public colorAttributeSelection(product: AddOnCard, defAttribute: AvailabilityObject): void {
		let removedSelection = false;
		// logic to change picture if color was changed
		let imageUrl = `https://dks.scene7.com/is/image/dkscdn/${product.id}`;
		let sanitizedColor = defAttribute.value.replace(/\//g, '_');
		sanitizedColor = sanitizedColor.replace(/ /g, '_');
		imageUrl = `${imageUrl}_${sanitizedColor}`;
		product.imageUrl = `${imageUrl}_is?$UTPMain$&op_sharpen=1&qlt=80`;

		// if there are other attributes, disable them based on color selection
		for (
			let otherDefAttrIndex = 0, otherDefAttrLength = product.otherDefAttributes.length;
			otherDefAttrIndex < otherDefAttrLength;
			otherDefAttrIndex++
		) {
			// disable all attr now so in the next step we can enable only the available ones
			product.otherDefAttributes[otherDefAttrIndex].disabled = true;
			// loop through all available combinations to see if any combinations exist with the selected "otherAttribute"
			for (
				let availabiltyIdex = 0, availabilityLength = product.availableCombinations.length;
				availabiltyIdex < availabilityLength;
				availabiltyIdex++
			) {
				// if color matches and other def attr matches, enable the other option
				if (
					defAttribute.value === product.availableCombinations[availabiltyIdex].color &&
					product.otherDefAttributes[otherDefAttrIndex].value ===
						product.availableCombinations[availabiltyIdex].otherDefAttributes
				) {
					product.otherDefAttributes[otherDefAttrIndex].disabled = false;
				}
			}
			// if we're disabling the otherAttr that was selected, de-selected it
			if (
				product.otherDefAttributes[otherDefAttrIndex].value ===
					product.otherDefAttributesValue &&
				product.otherDefAttributes[otherDefAttrIndex].disabled
			) {
				product.otherDefAttributesValue = undefined;
				product.otherDefAttributesSelected = false;
				removedSelection = true;
			}
		}

		// select color
		for (
			let defAttributeIndex = 0, defAttrlength = product.defAttributes.length;
			defAttributeIndex < defAttrlength;
			defAttributeIndex++
		) {
			if (product.defAttributes[defAttributeIndex].name.toLowerCase() === 'color') {
				const colorDefAttribute = product.defAttributes[defAttributeIndex];
				for (
					let colorAttributeIndex = 0, length = colorDefAttribute.availabilityMap.length;
					colorAttributeIndex < length;
					colorAttributeIndex++
				) {
					// set availabilityMap that the color is selected and set the attribute overall that it has been selected
					if (
						colorDefAttribute.availabilityMap[colorAttributeIndex].value ===
						defAttribute.value
					) {
						colorDefAttribute.availabilityMap[colorAttributeIndex].selected = true;
						colorDefAttribute.selected = true;
						colorDefAttribute.value = defAttribute.value;
					} else {
						// deselect all the other colors if any were selected
						colorDefAttribute.availabilityMap[colorAttributeIndex].selected = false;
					}
					if (removedSelection) {
						colorDefAttribute.availabilityMap[colorAttributeIndex].disabled = false;
					}
				}
			}
		}

		this.resolveSkuCheck(product);
	}

	public selectOtherAttribute(event: any, addOnProduct: AddOnCard): void {
		// reset options
		if (event.target.options) {
			for (let i = 0, length = event.target.options.length; i < length; ++i) {
				event.target.options[i].innerText = event.target.options[i].value;
			}
		}

		const selectedValue = event.target.value;
		if (selectedValue !== 'Options' && Array.isArray(addOnProduct.otherDefAttributes)) {
			// select option
			for (
				let otherDefAttrIndex = 0, length = addOnProduct.otherDefAttributes.length;
				otherDefAttrIndex < length;
				otherDefAttrIndex++
			) {
				if (selectedValue === addOnProduct.otherDefAttributes[otherDefAttrIndex].value) {
					addOnProduct.otherDefAttributes[otherDefAttrIndex].selected = true;
					addOnProduct.otherDefAttributesSelected = true;
					addOnProduct.otherDefAttributesValue = selectedValue;
					if (
						event.target.selectedOptions &&
						event.target.selectedOptions[0] !== undefined
					) {
						event.target.selectedOptions[0].innerText =
							addOnProduct.otherDefAttributes[otherDefAttrIndex].stringValue;
					}
				}
			}

			// check to see if we need to disable color
			// if there are other attributes, disable them based on color selection
			for (
				let defAttributeIndex = 0, defAttributeLength = addOnProduct.defAttributes.length;
				defAttributeIndex < defAttributeLength;
				defAttributeIndex++
			) {
				if (addOnProduct.defAttributes[defAttributeIndex].name.toLowerCase() === 'color') {
					const colorDefAttribute = addOnProduct.defAttributes[defAttributeIndex];
					for (
						let colorAttributeIndex = 0,
							colorAttributeLength = colorDefAttribute.availabilityMap.length;
						colorAttributeIndex < colorAttributeLength;
						colorAttributeIndex++
					) {
						const currentColor = colorDefAttribute.availabilityMap[colorAttributeIndex];
						currentColor.disabled = true;
						for (
							let availabiltyIdex = 0,
								availablityLength = addOnProduct.availableCombinations.length;
							availabiltyIdex < availablityLength;
							availabiltyIdex++
						) {
							// if color matches and other def attr matches, enable the other option
							if (
								currentColor.value ===
									addOnProduct.availableCombinations[availabiltyIdex].color &&
								selectedValue ===
									addOnProduct.availableCombinations[availabiltyIdex]
										.otherDefAttributes
							) {
								currentColor.disabled = false;
							}
						}
					}
				}
			}
			this.resolveSkuCheck(addOnProduct);
		} else {
			addOnProduct.otherDefAttributesSelected = false;
			addOnProduct.otherDefAttributesValue = undefined;
			addOnProduct.skuItem = undefined;
			addOnProduct.shownPrice = addOnProduct.productPrice;
		}
	}

	public resolveSkuCheck(addOnProduct: AddOnCard): void {
		if (!this.areAllSelectionsMade(addOnProduct)) {
			addOnProduct.skuItem = undefined;
			addOnProduct.shownPrice = addOnProduct.productPrice;
			return;
		}

		let currentAvailableCombinations = Object.assign([], addOnProduct.availableCombinations);
		if (addOnProduct.hasColorAttribute && addOnProduct.defAttributes[0].selected === true) {
			currentAvailableCombinations = currentAvailableCombinations.filter(
				(element) => element.color === addOnProduct.defAttributes[0].value
			);
		}

		if (addOnProduct.hasOtherAttributes && addOnProduct.otherDefAttributesSelected) {
			currentAvailableCombinations = currentAvailableCombinations.filter(
				(element) => element.otherDefAttributes === addOnProduct.otherDefAttributesValue
			);
		}

		if (
			Array.isArray(currentAvailableCombinations) &&
			currentAvailableCombinations.length === 1
		) {
			addOnProduct.skuItem = currentAvailableCombinations[0].sku;

			const addOnPrice =
				'prices' in addOnProduct.skuItem
					? addOnProduct.skuItem.prices
					: addOnProduct.skuItem.price;

			addOnProduct.shownPrice = {
				isSeePriceInCart: addOnPrice.offerPrice > addOnPrice.listPrice,
				isRangePrice: false,
				lowestPrice: addOnPrice.offerPrice,
				highestPrice: undefined
			};
		} else {
			addOnProduct.skuItem = undefined;
			addOnProduct.shownPrice = addOnProduct.productPrice;
		}

		addOnProduct.error = false;
	}

	public areAllSelectionsMade(addOnProduct: AddOnCard): boolean {
		if (addOnProduct.hasColorAttribute && addOnProduct.hasOtherAttributes) {
			return (
				Boolean(addOnProduct.defAttributes[0].selected) &&
				Boolean(addOnProduct.otherDefAttributesSelected)
			);
		}

		if (addOnProduct.hasColorAttribute) {
			return Boolean(addOnProduct.defAttributes[0].selected);
		}

		if (addOnProduct.hasOtherAttributes) {
			return Boolean(addOnProduct.otherDefAttributesSelected);
		}

		return true;
	}

	public generateDefaultAddOnCardData<T extends ProductSku | NestProductSKU>(): AddOnCard<T> {
		return {
			id: undefined,
			title: undefined,
			skuItem: undefined,
			defAttributes: [],
			imageUrl: undefined,
			buttonText: 'ADD TO CART',
			disable: false,
			state: 'active',
			// init to -1 so if something goes wrong while setting data we wont display
			rank: -1,
			pdpSeoUrl: undefined,
			availableCombinations: [],
			error: false,
			hasOtherAttributes: undefined,
			productPrice: undefined,
			shownColorValues: undefined,
			shownPrice: undefined,
			hasColorAttribute: undefined,
			otherDefAttributes: undefined,
			otherDefAttributesValue: undefined,
			otherDefAttributesSelected: undefined,
			selected: false,
			currentPdpProduct: false
		};
	}

	trackByAddOnCard(index: number, item: AddOnCard): string {
		if (!item.skuItem) {
			return index.toString() + item.id;
		}
		const skuItemId = 'id' in item.skuItem ? item.skuItem.id : item.skuItem.partNumber;
		return index.toString() + item.id + skuItemId;
	}

	trackByFn(index: number, item: any): string {
		return index.toString() + item.value;
	}

	trackByCardDataList(index: number, item: AddOnCard): string {
		if (!item.skuItem) {
			return index.toString();
		}
		const skuItemId = 'id' in item.skuItem ? item.skuItem.id : item.skuItem.partNumber;
		return index.toString() + skuItemId;
	}
}
